import React, { useEffect, useState } from "react";
import Select from "react-select";
import { getPost } from "../utils/commHelper";
import {
  getUrlWithFilters,
  createFilterCondition,
  createEmptyFilterGroup,
} from "./filterUtils";

// Resolve a display label from a reference value that may be an object
// ({_id, title}) or a plain id string. Falls back to _id when title is empty.
const getRefLabel = (item) => {
  if (item && typeof item === "object") return item.title || item._id;
  return item; // plain id string
};

const getRefId = (item) =>
  item && typeof item === "object" ? item._id : item;

export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

const SelectCell = ({
  value,
  onCellChange,
  refItems,
  refColumn,
  refRes,
  width,
  isFilter,
  apiTarget = false,
  readOnly = false,
}) => {
  const [options, setOptions] = useState(
    value ? [{ value: getRefId(value), label: getRefLabel(value) }] : []
  );
  const [loader, setLoader] = useState(false);
  const [changedValue, setChagedValue] = useState("");
  const debouncedValue = useDebounce(changedValue, 1000);
  useEffect(() => {
    if (debouncedValue.length > 0) {
      setLoader(true);
      //   createOptions(refItems);
      getFilteredDataFromServer(changedValue).then(() => {
        setLoader(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounced search: must fire only when debouncedValue settles, not on every changedValue keystroke
  }, [debouncedValue]);

  const handleInputChange = (event) => {
    if (event.length > 0) {
      setChagedValue(event);
    }
  };

  const getDataFromServer = async () => {
    if (refRes.current[refColumn]) {
      createOptions(refRes.current[refColumn]);
    } else {
      const res = await getPost(
        "/getInterfaceTitlesRef",
        {
          tableName: refColumn,
        },
        apiTarget
      ).then((response) => response.data);
      refRes.current = { ...refRes.current, [refColumn]: res };

      createOptions(res);
    }
  };

  const getFilteredDataFromServer = async (value) => {
    const res = await getPost(
      "/getInterfaceFilteredData",
      {
        tableName: refColumn,
        keys: value,
      },
      apiTarget
    ).then((response) => response.data);
    refRes.current = { ...refRes.current, [refColumn]: res };
    createOptions(res);
  };

  const createOptions = (items) => {
    let newOptions = [...options];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      newOptions.push({ value: getRefId(item), label: getRefLabel(item) });
    }
    const seen = new Set();

    newOptions = newOptions.filter((el) => {
      const duplicate = seen.has(el.value);
      seen.add(el.value);
      return !duplicate;
    });
    setOptions(newOptions);
  };

  const handleNavigate = () => {
    const itemId = getRefId(value);
    if (!refColumn || !itemId) return;
    const filterGroup = createEmptyFilterGroup("AND");
    filterGroup.conditions = [createFilterCondition("_id", "equals", itemId)];
    window.location.href = getUrlWithFilters(refColumn, filterGroup);
  };

  return (
    options !== undefined && (
      <div>
        <Select
          className={isFilter ? "basic-multi-select" : "basic-single"}
          classNamePrefix="select"
          placeholder=""
          menuPortalTarget={document.body}
          isDisabled={readOnly}
          styles={{
            multiValueRemove: (base) => ({
              ...base,
              border: `1px dotted black`,
              height: "100%",
            }),
            menu: (base) => ({
              ...base,
              zIndex: "99999",
            }),
            menuPortal: (base) => ({
              ...base,
              zIndex: "99999",
            }),
            control: (provided, state) => ({
              ...provided,
              boxShadow: "none",
              border: "none",
              width: width || 378,
              minHeight: 42,
              backgroundColor: readOnly ? "#f5f5f5" : "transparent",
              cursor: readOnly ? "not-allowed" : "default",
            }),
            // Read-only must stay readable. react-select fades a disabled value to a light
            // grey, and this cell used to dim the whole control on top of that, which left
            // the text barely legible on the read-only link. "Not editable" is signalled by
            // the grey background and the cursor instead.
            singleValue: (provided) => ({
              ...provided,
              color: "hsl(0, 0%, 20%)",
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: "hsl(0, 0%, 20%)",
            }),
          }}
          onInputChange={handleInputChange}
          onChange={(event) => {
            if (!onCellChange || readOnly) return; // Skip if read-only or no handler provided
            if (isFilter) {
              onCellChange(
                event && event.length > 0
                  ? event.map((i) => ({
                      _id: i.value,
                      title: i.label || i.value,
                    }))
                  : { _id: null }
              );
            } else {
              onCellChange(
                event
                  ? { _id: event.value, title: event.label || event.value }
                  : { _id: null }
              );
            }
          }}
          onMenuOpen={async () => {
            if (readOnly) return; // Prevent menu from opening in read-only mode
            setLoader(true);
            createOptions(refItems);
            await getDataFromServer();
            setLoader(false);
          }}
          isLoading={loader}
          defaultValue={options[0]}
          isClearable={!readOnly}
          isSearchable={!readOnly}
          closeMenuOnSelect={!isFilter}
          options={options}
          isMulti={isFilter}
        />
        {value && (
          <div
            style={{
              fontSize: "12px",
              marginLeft: "10px",
              color: "#0202b1",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span>{refColumn === "users" ? value?.user : value?._id}</span>
            {!isFilter && (
              <span
                onClick={handleNavigate}
                title="View this referenced item in its table"
                style={{
                  cursor: "pointer",
                  color: "#1976d2",
                  fontSize: "13px",
                  userSelect: "none",
                }}
              >
                ↗
              </span>
            )}
          </div>
        )}
      </div>
    )
  );
};

export default SelectCell;
